import { FormEvent, useEffect, useRef, useState } from "react"
import { ArchieRobot } from "@/components/ArchieRobot"
import { MarkdownRenderer } from "@/components/MarkdownRenderer"
import { useArchieChat } from "@/hooks/useArchieChat"
import { getJson } from "@/lib/api"
import {
  ARCHIE_NUDGES,
  ARCHIE_SIGNUP_PITCH_ASSIGNMENT_URL,
  ARCHIE_SIGNUP_PITCH_EXPERIMENT,
  ARCHIE_SIGNUP_PITCH_VARIANT,
  captureArchieEvent,
  dismissNudge,
  fieldTestAssignmentUrl,
  localizedCopy,
  nudgeDismissed,
  shouldHideArchie,
  trackArchiePageview,
} from "@/lib/archieContext"
import { cn } from "@/lib/cn"
import type { FieldTestAssignmentResponse } from "@/types/reportsSignupWall"

const NUDGE_DELAY_MS = 10_000
const NUDGE_SCROLL_RATIO = 0.25

export interface ArchieWidgetProps {
  signedIn?: boolean
}

export function ArchieWidget({ signedIn = false }: ArchieWidgetProps): JSX.Element | null {
  const { open, setOpen, locale, loading, messages, isStreaming, error, ensureChat, sendMessage } =
    useArchieChat()
  const [visible, setVisible] = useState(false)
  const [showNudge, setShowNudge] = useState(false)
  const [draft, setDraft] = useState("")
  const [hiddenRoute, setHiddenRoute] = useState(() => shouldHideArchie())
  const panelRef = useRef<HTMLDivElement | null>(null)
  const nudgedRef = useRef(false)

  useEffect(() => {
    const onNavigate = () => {
      trackArchiePageview(window.location.pathname)
      setHiddenRoute(shouldHideArchie(window.location.pathname))
    }
    onNavigate()
    setVisible(true)
    captureArchieEvent("archie_widget_shown")

    document.addEventListener("turbo:load", onNavigate)
    return () => document.removeEventListener("turbo:load", onNavigate)
  }, [])

  useEffect(() => {
    if (hiddenRoute || open || nudgeDismissed() || nudgedRef.current) return

    let cancelled = false
    let timer: number | undefined

    const hasRealTranscript = messages.some((message) => message.id > 0)

    const revealNudge = () => {
      if (cancelled || nudgedRef.current || nudgeDismissed()) return
      nudgedRef.current = true
      setShowNudge(true)
      captureArchieEvent("archie_nudge_shown")
    }

    const revealPitch = () => {
      if (cancelled || nudgedRef.current || nudgeDismissed() || hasRealTranscript) return
      nudgedRef.current = true
      setShowNudge(false)
      dismissNudge()
      setOpen(true)
      captureArchieEvent("archie_opened")
      captureArchieEvent("archie_signup_pitch_shown", {
        experiment: ARCHIE_SIGNUP_PITCH_EXPERIMENT,
        variant: ARCHIE_SIGNUP_PITCH_VARIANT,
      })
      void ensureChat()
    }

    const armTriggers = (onReveal: () => void) => {
      timer = window.setTimeout(onReveal, NUDGE_DELAY_MS)
      const onScroll = () => {
        const doc = document.documentElement
        const max = doc.scrollHeight - window.innerHeight
        if (max <= 0) return
        if (window.scrollY / max >= NUDGE_SCROLL_RATIO) onReveal()
      }
      window.addEventListener("scroll", onScroll, { passive: true })
      return () => {
        if (timer != null) window.clearTimeout(timer)
        window.removeEventListener("scroll", onScroll)
      }
    }

    if (signedIn) {
      return armTriggers(revealNudge)
    }

    let disarm: (() => void) | undefined
    void (async () => {
      try {
        const data = await getJson<FieldTestAssignmentResponse>(
          fieldTestAssignmentUrl(ARCHIE_SIGNUP_PITCH_ASSIGNMENT_URL)
        )
        if (cancelled) return
        const treatment = data.enrolled && data.variant === ARCHIE_SIGNUP_PITCH_VARIANT
        disarm = armTriggers(treatment ? revealPitch : revealNudge)
      } catch {
        if (cancelled) return
        disarm = armTriggers(revealNudge)
      }
    })()

    return () => {
      cancelled = true
      disarm?.()
    }
  }, [ensureChat, hiddenRoute, messages, open, setOpen, signedIn])

  useEffect(() => {
    if (!open || !panelRef.current) return
    panelRef.current.scrollTop = panelRef.current.scrollHeight
  }, [open, messages, isStreaming])

  if (!visible || hiddenRoute) return null

  const nudgeText = localizedCopy(ARCHIE_NUDGES, locale)

  const handleOpen = async () => {
    setOpen(true)
    setShowNudge(false)
    dismissNudge()
    captureArchieEvent("archie_opened")
    await ensureChat()
  }

  const handleClose = () => {
    setOpen(false)
    dismissNudge()
  }

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault()
    const value = draft
    setDraft("")
    await sendMessage(value)
  }

  return (
    <div className="pointer-events-none fixed bottom-5 right-5 z-[60] flex flex-col items-end gap-3">
      {open && (
        <div
          className={cn(
            "pointer-events-auto flex w-[min(100vw-2rem,22rem)] flex-col overflow-hidden",
            "rounded-2xl border border-[#1f3d2b]/15 bg-[#f7faf7] shadow-xl",
            "origin-bottom-right animate-[archie-panel-in_280ms_ease-out]"
          )}
          role="dialog"
          aria-label="Archie chat"
        >
          <div className="flex items-center gap-3 bg-[#1f3d2b] px-4 py-3 text-[#e7f2ea]">
            <ArchieRobot size="sm" speaking />
            <div className="min-w-0 flex-1">
              <p className="font-[Georgia,Times,serif] text-lg leading-none">Archie</p>
              <p className="text-xs text-[#e7f2ea]/80">Arc Research helper</p>
            </div>
            <button
              type="button"
              onClick={handleClose}
              className="rounded px-2 py-1 text-sm text-[#e7f2ea]/90 hover:bg-white/10"
              aria-label="Close Archie"
            >
              ✕
            </button>
          </div>

          <div ref={panelRef} className="max-h-80 space-y-3 overflow-y-auto px-3 py-3">
            {loading && messages.length === 0 && (
              <p className="text-sm text-[#1f3d2b]/70">Starting Archie…</p>
            )}
            {messages
              .filter((m) => m.role === "user" || m.role === "assistant")
              .map((message) => (
                <div
                  key={message.id}
                  className={cn(
                    "max-w-[90%] rounded-xl px-3 py-2 text-sm leading-relaxed",
                    message.role === "user"
                      ? "ml-auto bg-[#1f3d2b] text-[#e7f2ea]"
                      : "mr-auto bg-white text-[#1f3d2b] shadow-sm"
                  )}
                >
                  {message.content ? (
                    <div
                      className={cn(
                        "prose prose-sm max-w-none",
                        message.role === "user" && "prose-invert"
                      )}
                    >
                      <MarkdownRenderer content={message.content} />
                    </div>
                  ) : (
                    <span className="opacity-60">…</span>
                  )}
                </div>
              ))}
            {error && <p className="text-sm text-red-700">{error}</p>}
          </div>

          <form onSubmit={handleSubmit} className="border-t border-[#1f3d2b]/10 p-3">
            <div className="flex gap-2">
              <input
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder={nudgeText}
                disabled={isStreaming || loading}
                className="min-w-0 flex-1 rounded-lg border border-[#1f3d2b]/20 bg-white px-3 py-2 text-sm text-[#1f3d2b] outline-none focus:border-[#1f3d2b]"
              />
              <button
                type="submit"
                disabled={isStreaming || loading || !draft.trim()}
                className="rounded-lg bg-[#c4a35a] px-3 py-2 text-sm font-medium text-[#1f3d2b] disabled:opacity-50"
              >
                Send
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="pointer-events-auto relative">
        {showNudge && !open && (
          <button
            type="button"
            onClick={handleOpen}
            className="absolute bottom-[4.5rem] right-0 whitespace-nowrap rounded-full bg-[#1f3d2b] px-3 py-2 text-sm text-[#e7f2ea] shadow-lg animate-[archie-bubble-in_320ms_ease-out]"
          >
            {nudgeText}
          </button>
        )}
        <button
          type="button"
          onClick={open ? handleClose : handleOpen}
          className="archie-float rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#c4a35a]"
          aria-label={open ? "Close Archie" : "Open Archie"}
        >
          <ArchieRobot speaking={open || showNudge} />
        </button>
      </div>

      <style>{`
        @keyframes archie-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes archie-panel-in {
          from { opacity: 0; transform: translateY(12px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes archie-bubble-in {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .archie-float {
          animation: archie-float 3.2s ease-in-out infinite;
        }
      `}</style>
    </div>
  )
}
